import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import { login, verifyTokenApi, systemInfo } from "../_request";
import { useAuth } from "context";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import { setGtmCode } from "../../../core/AuthHelper";
import SignInWithGoogle from "./SignInWithGoogle";

import { useAnalyticsEvent } from "hooks/useAnalyticsEvent";
import { AnalyticsEvents } from "core/constant";

function PasswordSignIn({ setAuthSection }) {
  const landingAccessToken =
    new URL(window.location.href).searchParams.get("landing_page_tokens") || null;
  const { saveAuthData, showSnackbarWithMessage } = useAuth();
  const navigate = useNavigate();
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [loginLoader, setLoginLoader] = useState(false);
  const { trackCustomEvent } = useAnalyticsEvent();

  const [signIn, setSignIn] = useState({
    email: "",
    password: "",
  });

  const isEmailValid = (email) => {
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailPattern.test(email);
  };

  const handleFormFieldChange = (event) => {
    const { name, value } = event.target;
    setSignIn((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  useEffect(() => {
    updateButtonDisabledState();
  }, [signIn]);

  useEffect(() => {
    if (landingAccessToken) {
      saveAuthData(JSON.parse(landingAccessToken));
      const gtmCode = new URL(window.location.href).searchParams.get("s") || null;
      if (gtmCode) {
        setGtmCode(gtmCode); // Save GTM code to local storage
      }
      navigate("/dashboard");
      showSnackbarWithMessage({
        type: "success",
        message: "Welcome to Catering Rewards! Head to Profile Page for further settings!",
      });
    }
  }, [landingAccessToken]);

  const updateButtonDisabledState = () => {
    const shouldDisableButton = signIn.email.trim() === "" || signIn.password.trim() === "";
    setButtonDisabled(shouldDisableButton);
  };

  const handleSubmit = async () => {
    try {
      setLoginLoader(true);
      const loginResponse = await login({ ...signIn, userType: "CUSTOMER" });
      const { success, data, message } = loginResponse.data;
      if (success) {
        const authData = data.tokens;
        const verifiedCustomer = await saveAuthData(authData);
        await trackCustomEvent(
          AnalyticsEvents.LOGIN,
          {
            method: "password",
            auth_origin: "customer_portal",
            user_id: verifiedCustomer.id,
            user_role: verifiedCustomer.role,
            user_type: verifiedCustomer.userType,
          },
          false
        );

        if (!!verifiedCustomer && verifiedCustomer.verifiedAt) {
          await systemInfo();
          navigate("/dashboard");
          showSnackbarWithMessage({
            type: "success",
            message: "Login successful. Welcome to the Catering Rewards!",
          });
          return;
        } else if (!!verifiedCustomer) {
          await systemInfo();
          navigate("/authentication/verify-customer");
          showSnackbarWithMessage({
            type: "success",
            message: "You're in! To complete the process, please verify your email.",
          });
          return;
        } else {
          showSnackbarWithMessage({
            type: "error",
            message: "something went wrong",
          });
        }
        setLoginLoader(false);
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoginLoader(false);
    }
  };

  return (
    <>
      <Card sx={{ margin: 2, marginLeft: 3, maxWidth: "548px" }}>
        <MDBox mx={2} p={2} textAlign="center" minWidth={{ xs: "320px", xl: "380px" }}>
          <MDTypography
            sx={{ color: "#101828", fontSize: "28px" }}
            variant="h4"
            mt={1}
            fontWeight="medium"
          >
            Log in & start earning
          </MDTypography>
        </MDBox>

        <MDBox pt={2} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                name="email"
                fullWidth
                onChange={handleFormFieldChange}
                error={!isEmailValid(signIn.email)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                name="password"
                fullWidth
                onChange={handleFormFieldChange}
              />
              <Link
                style={{
                  textAlign: "right",
                  color: "#475467",
                  fontSize: 14,
                  fontWeight: "normal",
                  float: "right",
                  marginBottom: 16,
                  marginTop: 3,
                }}
                to="/authentication/forgot-password"
              >
                Forgot Password
              </Link>
            </MDBox>

            <MDBox mt={2}>
              <MDButton
                variant="gradient"
                color="success"
                fullWidth
                disabled={buttonDisabled}
                onClick={handleSubmit}
              >
                log in
              </MDButton>
            </MDBox>

            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
                mt: "16px",
                mb: 2,
              }}
            >
              <MDBox
                sx={{
                  height: "1px",
                  flex: 1,
                  background: "#AFAFAF",
                }}
              />
              <MDTypography
                variant="button"
                sx={{
                  color: "#AFAFAF",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                or
              </MDTypography>
              <MDBox
                sx={{
                  height: "1px",
                  flex: 1,
                  background: "#AFAFAF",
                }}
              />
            </MDBox>

            <MDButton
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => setAuthSection("code")}
              sx={{
                borderRadius: "8px",
                py: 1.5,
                background: "#E8E8E8",
                borderColor: "#E8E8E8",
                marginTop: "1rem",
                color: "#475467",
                textTransform: "none",
                fontWeight: "500",
                fontSize: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                "&:hover": {
                  background: "#D1D1D1",
                  borderColor: "#D1D5DB",
                  color: "#101828",
                },
              }}
            >
              Login with code
            </MDButton>

            <SignInWithGoogle />

            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              style={{ marginTop: "24px" }}
            >
              <span
                style={{ fontSize: 12, maxWidth: "360px", color: "#6B6B6B", LineHeight: "20px" }}
                x
              >
                By proceeding, you consent to get calls, SMS messages & emails from Catering Rewards
                and its affiliates to the number provided. Reply “STOP” to opt out. Message & Data
                rates may apply.
              </span>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {loginLoader && <SimpleBackdrop />}
    </>
  );
}

export default PasswordSignIn;

PasswordSignIn.propTypes = {
  setAuthSection: PropTypes.func.isRequired,
};
